import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Repeat as RepeatIcon } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { i: 0, label: "Mon" }, { i: 1, label: "Tue" }, { i: 2, label: "Wed" },
  { i: 3, label: "Thu" }, { i: 4, label: "Fri" }, { i: 5, label: "Sat" },
  { i: 6, label: "Sun" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function RepeatJobDialog({
  jobId,
  defaultStart,
  size = "sm",
  variant = "outline",
  buttonClassName = "",
  onCreated,
  triggerTestId = "repeat-btn",
  showIcon = true,
  triggerIcon = null,        // pass a JSX icon to render an icon-only trigger
  triggerLabel = "Repeat",   // set to "" together with triggerIcon for icon-only
  triggerTitle,
}) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(defaultStart || todayISO());
  const [end, setEnd] = useState(addDays(defaultStart || todayISO(), 6));
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [busy, setBusy] = useState(false);

  const toggle = (i) => {
    setDays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort());
  };

  const submit = async () => {
    if (!start || !end) return toast.error("Pick start and end dates.");
    if (new Date(end) < new Date(start)) return toast.error("End date must be on or after start.");
    if (days.length === 0) return toast.error("Pick at least one day of the week.");
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/repeat`, {
        start_date: start, end_date: end, days_of_week: days, reset_status_to_new: true,
      });
      toast.success(`Created ${r.data.created} repeat job${r.data.created === 1 ? "" : "s"}.`);
      setOpen(false);
      onCreated?.(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          className={`rounded-sm ${buttonClassName}`}
          data-testid={triggerTestId}
          onClick={(e) => e.stopPropagation()}
          title={triggerTitle || (triggerLabel ? undefined : "Repeat this job")}
        >
          {triggerIcon ? triggerIcon : (showIcon ? <RepeatIcon className="h-4 w-4 mr-2" /> : null)}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-sm" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Repeat this job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Start date</Label>
              <Input data-testid="repeat-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">End date</Label>
              <Input data-testid="repeat-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Days of the week</Label>
            <div className="flex flex-wrap gap-3 bg-slate-50 border border-slate-200 rounded-sm p-3">
              {DAYS.map((d) => (
                <label key={d.i} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    data-testid={`repeat-day-${d.label.toLowerCase()}`}
                    checked={days.includes(d.i)}
                    onCheckedChange={() => toggle(d.i)}
                  />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11px]">
              <button type="button" className="underline text-slate-600 hover:text-slate-900" onClick={() => setDays([0,1,2,3,4])}>Mon–Fri</button>
              <span className="text-slate-300">·</span>
              <button type="button" className="underline text-slate-600 hover:text-slate-900" onClick={() => setDays([0,1,2,3,4,5,6])}>Every day</button>
              <span className="text-slate-300">·</span>
              <button type="button" className="underline text-slate-600 hover:text-slate-900" onClick={() => setDays([5,6])}>Weekends</button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Creates one new job per matching day, copying all details (client, site, subcontractor, rate, charge, instructions). Each copy starts with status <span className="font-medium">New</span> and can be edited independently.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-testid="repeat-confirm" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} disabled={busy}>
            {busy ? "Creating..." : "Create repeats"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
